import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'
import { toast } from 'sonner'
import { Check, Navigation, Info } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog'
import { Input } from '../ui/input'
import { Button } from '../ui/button'
import { FormError } from '../ui/FormError'
import { Spinner } from '@/app/lib/loader/loader'
import { navigationService } from '../../services/navigation.service'

const menuSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido').max(255),
  code: z
    .string()
    .min(1, 'El código es requerido')
    .max(60)
    .regex(/^[A-Z0-9_]+$/i, 'Solo letras, números y guiones bajos'),
  is_active: z.boolean(),
})

type MenuFormValues = z.infer<typeof menuSchema>

interface NavigationMenuModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  menuId: number | null
  onSuccess: () => void
}

export function NavigationMenuModal({
  open,
  onOpenChange,
  menuId,
  onSuccess,
}: NavigationMenuModalProps) {
  const isEditing = !!menuId
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<MenuFormValues>({
    resolver: zodResolver(menuSchema),
    defaultValues: { name: '', code: '', is_active: true },
  })

  const isActiveValue = watch('is_active')

  const { data: menu, isLoading } = useQuery({
    queryKey: ['navigation-menu', menuId],
    queryFn: () => navigationService.getMenu(menuId!),
    enabled: !!menuId && open,
  })

  useEffect(() => {
    if (!open) {
      reset({ name: '', code: '', is_active: true })
    }
  }, [open, reset])

  useEffect(() => {
    if (menu && open) {
      reset({ name: menu.name, code: menu.code, is_active: menu.is_active })
    }
  }, [menu, open, reset])

  const onSubmit = async (data: MenuFormValues) => {
    try {
      const payload = { ...data, code: data.code.toUpperCase() }
      if (isEditing && menuId) {
        await navigationService.updateMenu(menuId, payload)
        queryClient.invalidateQueries({ queryKey: ['navigation-menu', menuId] })
        toast.success('Menú actualizado exitosamente')
      } else {
        await navigationService.createMenu(payload)
        toast.success('Menú creado exitosamente')
      }
      onSuccess()
    } catch {
      toast.error('Ocurrió un error al guardar el menú')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-card-foreground">
              <Navigation className="h-5 w-5 text-primary" />
              {isEditing ? 'Editar Menú' : 'Nuevo Menú'}
            </DialogTitle>
            <DialogDescription className="text-card-description">
              {isEditing
                ? 'Modifica los datos del menú de navegación.'
                : 'Crea un nuevo menú de navegación para la tienda en línea.'}
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-8 w-8" />
            </div>
          ) : (
            <div className="space-y-5 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-card-foreground">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <Input placeholder="Ej. Menú Principal" {...register('name')} />
                <FormError message={errors.name?.message} />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-card-foreground">
                  Código <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder="Ej. MAIN_NAV, FOOTER"
                  {...register('code')}
                  onChange={(e) =>
                    setValue('code', e.target.value.toUpperCase(), {
                      shouldValidate: true,
                    })
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Identificador único. Solo letras mayúsculas, números y guiones bajos.
                </p>
                <FormError message={errors.code?.message} />
              </div>

              {/* Nota: menú activo único */}
              <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-xs text-blue-800">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-500" />
                <p>
                  Solo un menú puede estar <strong>activo</strong> a la vez.
                  El menú activo es el que aparece en el <strong>Header</strong> de la tienda en línea.
                </p>
              </div>

              <div className="flex items-center gap-3 pt-1">
                <button
                  type="button"
                  role="switch"
                  aria-checked={isActiveValue}
                  onClick={() => setValue('is_active', !isActiveValue)}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${
                    isActiveValue ? 'bg-primary' : 'bg-slate-200'
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      isActiveValue ? 'translate-x-2' : '-translate-x-2'
                    }`}
                  />
                </button>
                <span className="text-sm font-medium text-card-foreground">Menú Activo</span>
              </div>
              {isActiveValue && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  ⚠️ Al guardar con <strong>Menú Activo</strong>, el menú que estaba activo anteriormente será desactivado automáticamente.
                </p>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 sm:justify-end mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || isLoading}
              className="bg-primary hover:bg-primary/90"
            >
              {isSubmitting ? (
                <Spinner className="mr-2 h-4 w-4" />
              ) : (
                <Check className="mr-2 h-4 w-4" />
              )}
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
